/**
 * Mom's texts, as a branching conversation. When she texts, the heroine takes
 * out her phone and reads (walking slowly) until she answers. A calm answer
 * ends the conversation; a worrying one makes Mom text back with more
 * questions, which keeps her eyes on the screen for longer. Ignoring a text
 * (putting the phone away, or letting the clock run out) lets the stranger
 * close in.
 */

export const MOM_NAME = 'お母さん · Mom';
export const TYPE_TIME = 3.2;
/** How long Mom's "typing…" bubble shows before her message lands. */
const MOM_TYPING_TIME = 2.2;
const FIRST_DEADLINE = 20;
const FOLLOW_UP_DEADLINE = 15;
const MISSED_CALL_INTERVAL = 20;

export type MomOption = {
  text: string;
  /** Mom answers with another question (more time on the phone). */
  next?: string;
  /** Mom's last word; the conversation ends. */
  end?: string;
};
export type MomNode = {
  text: string;
  options: [MomOption, MomOption];
  /** Mom's phone dies mid-message: no nagging, and anything sent back is "Not delivered". */
  last?: boolean;
};
/** A night's conversation tree: node id -> message. Each level supplies its own. */
export type MomScript = Record<string, MomNode>;
type NodeId = string;

const FOLLOW_UPS = ['Hello?', "Why aren't you answering??", 'Please just answer me.'];

export type ChatMessage = { from: 'mom' | 'me' | 'system'; text: string };

export type MomView = {
  id: number;
  thread: ChatMessage[];
  /** Quick replies she can send right now (empty while Mom is typing / after the chat ends). */
  options: string[];
  timeLeft: number;
  timeTotal: number;
  /** 0..1 while she is typing a reply. */
  typing: number | null;
  typingText: string | null;
  momTyping: boolean;
};

export type MomHooks = {
  /**
   * A message from Mom landed. `first` = very first text of the night;
   * `kind` = a new conversation, a worried follow-up question to her answer,
   * a nag after being ignored, or Mom's last word.
   */
  received: (text: string, first: boolean, kind: 'new' | 'question' | 'nag' | 'end') => void;
  sent: (text: string) => void;
  /** The conversation is over (Mom had the last word). */
  ended: () => void;
  /** A deadline passed without a reply. `count` = total ignores this run. */
  ignored: (count: number) => void;
};

type Active = { id: number; node: NodeId; deadline: number; total: number; followUps: number };

export class MomTexts {
  private queue: { at: number; node: NodeId }[] = [];
  private script: MomScript = {};
  private active: Active | null = null;
  private typing: { choice: number; t: number } | null = null;
  private momTyping: { at: number; node?: NodeId; end?: string } | null = null;
  private thread: ChatMessage[] = [];
  private nextId = 1;
  private now = 0;
  private receivedAny = false;
  /** Her phone died: messages to her stop getting through, and she stops texting. */
  private silenced = false;
  ignoredTotal = 0;
  repliedTotal = 0;
  /** Extra exchanges caused by worrying answers. */
  extraExchanges = 0;

  constructor(private readonly hooks: MomHooks) {}

  reset(): void {
    this.queue = [];
    this.active = null;
    this.typing = null;
    this.momTyping = null;
    this.thread = [];
    this.ignoredTotal = 0;
    this.repliedTotal = 0;
    this.extraExchanges = 0;
    this.receivedAny = false;
    this.silenced = false;
    this.now = 0;
  }

  /** Mom goes quiet: nothing more is queued, and anything sent is "Not delivered". */
  silence(): void {
    this.silenced = true;
    this.queue = [];
    if (this.active) this.active.deadline = Infinity;
  }

  /** Use this night's conversation tree. */
  setScript(script: MomScript): void {
    this.script = script;
  }

  schedule(node: string, at: number): void {
    if (!this.script[node] || this.queue.some((q) => q.node === node)) return;
    this.queue.push({ at, node });
  }

  get texting(): boolean {
    return this.typing !== null;
  }

  /** A conversation is in progress: there is something to read or answer. */
  get conversationOpen(): boolean {
    return !!this.active || !!this.typing || !!this.momTyping;
  }

  /** Waiting on her answer. */
  get awaitingReply(): boolean {
    return !!this.active && !this.typing;
  }

  reply(choice: number, id?: number): boolean {
    if (!this.active || this.typing) return false;
    if (id !== undefined && id !== this.active.id) return false;
    if (choice < 0 || choice > 1) return false;
    this.typing = { choice, t: 0 };
    return true;
  }

  /** She put the phone away mid-reply: the half-typed message is dropped, Mom is still waiting. */
  cancelTyping(): void {
    this.typing = null;
  }

  update(now: number, dt: number): void {
    this.now = now;

    // Mom's reply lands after her typing bubble.
    if (this.momTyping && now >= this.momTyping.at) {
      const mt = this.momTyping;
      this.momTyping = null;
      if (mt.node) this.open(mt.node, FOLLOW_UP_DEADLINE, 'question');
      else if (mt.end) {
        this.thread.push({ from: 'mom', text: mt.end });
        this.hooks.received(mt.end, false, 'end');
        this.hooks.ended();
      }
    }

    if (this.typing && this.active) {
      this.typing.t += dt;
      if (this.typing.t >= TYPE_TIME) {
        const option = this.script[this.active.node].options[this.typing.choice];
        this.thread.push({ from: 'me', text: option.text });
        this.hooks.sent(option.text);
        this.repliedTotal += 1;
        if (option.next) this.extraExchanges += 1;
        if (this.silenced) {
          this.thread.push({ from: 'system', text: 'Not delivered' });
          this.hooks.ended();
        } else {
          this.momTyping = { at: now + MOM_TYPING_TIME, node: option.next, end: option.end };
        }
        this.active = null;
        this.typing = null;
      }
      return; // the clock doesn't run out while she's typing
    }

    if (this.active && now >= this.active.deadline) {
      this.ignoredTotal += 1;
      this.hooks.ignored(this.ignoredTotal);
      const a = this.active;
      const text = a.followUps < FOLLOW_UPS.length ? FOLLOW_UPS[a.followUps] : 'Missed call';
      a.followUps += 1;
      a.id = this.nextId++;
      a.total = a.followUps <= FOLLOW_UPS.length ? FOLLOW_UP_DEADLINE : MISSED_CALL_INTERVAL;
      a.deadline = now + a.total;
      this.thread.push({ from: text === 'Missed call' ? 'system' : 'mom', text });
      this.hooks.received(text, false, 'nag');
    }

    if (!this.conversationOpen) {
      const i = this.queue.findIndex((q) => now >= q.at);
      if (i >= 0) {
        const { node } = this.queue.splice(i, 1)[0];
        this.open(node, FIRST_DEADLINE, 'new');
      }
    }
  }

  private open(node: NodeId, deadline: number, kind: 'new' | 'question'): void {
    const text = this.script[node].text;
    this.active = { id: this.nextId++, node, deadline: this.now + deadline, total: deadline, followUps: 0 };
    this.thread.push({ from: 'mom', text });
    this.hooks.received(text, !this.receivedAny, kind);
    this.receivedAny = true;
    if (this.script[node].last) this.silence();
  }

  view(): MomView | null {
    if (this.thread.length === 0) return null;
    const a = this.active;
    const options = a && !this.typing ? this.script[a.node].options.map((o) => o.text) : [];
    const typingText = this.typing && a ? this.script[a.node].options[this.typing.choice].text : null;
    return {
      id: a?.id ?? -1,
      thread: this.thread.slice(-10),
      options,
      timeLeft: a && Number.isFinite(a.deadline) ? Math.max(0, a.deadline - this.now) : a ? a.total : 0,
      timeTotal: a?.total ?? 1,
      typing: this.typing ? Math.min(1, this.typing.t / TYPE_TIME) : null,
      typingText,
      momTyping: !!this.momTyping,
    };
  }
}
